/**
 * v3 only — “Studio” account hub. Layout and visuals are specific to v3; routes and guards match v1.
 */
import React, { useMemo, useCallback, useState } from 'react';
import { useAuthNavigation } from '../../../shared/navigation/useAuthNavigation';
import { View, Text, StyleSheet, Linking, Image } from 'react-native';
import Animated, { useSharedValue, useAnimatedScrollHandler } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIcon } from '../../../components/common/SvgIcon';
import { ButtonLoader } from '../../../components/load-main/ButtonLoader';
import { Clickable2 } from '../../../components/common/Clickable2';
import { SolidCtaClickable } from '../../../components/common/SolidCtaClickable';
import { useAuthStore } from '../../../shared/auth/store';

import { logout } from '../../../shared/auth';
import { useTranslation, getSubscriptionPlanTitleFromMerged, formatNumber, layoutLtrStyle, layoutRtlStyle } from '../../../shared/i18n';
import {
  useSubscriptionContext,
  getActiveMealDayProgressFromSubscription,
  resolveMergedSubscriptionImageUri,
  useSubscriptionPlans,
  type SubscriptionData,
} from '../../../data/domains/subscription';
import { appTheme, useThemeColors, type ThemeColors } from '../../../shared/theme';
import { androidScaleFont, androidScaleSize } from '../../../shared/theme/androidUiParity';
import { useCommunityFeatureEnabled, useConsultationsFeatureEnabled, useLoyaltyFeatureEnabled, useOnlineOrdersFeatureEnabled, useApiConfig, resolveOrganizationSocialLinks } from '../../../data/domains/config';
import { AnimatedSection } from '../../../components/common/AnimatedSection';
import { HUB_SECTION_PROPS } from '../../../shared/theme/studio/studioMotion';
import { studioCardShell, DASHBOARD_SECTION_GAP } from '../../../shared/theme/studio/dashboardConstants';
import { useRtl } from '../../../shared/i18n/useRtl';
import { ScreenHeader, useHubHeaderTopPadding, HeaderIconButton } from '../../../components/common/ScreenHeader';
import { TAB_HEADER_EXPANDED_HEIGHT } from '../../../shared/theme/styles/headerTokens';
import { isDevHubClient } from '../../../shared/config/applications';
import { ScreenShell } from '../../../shared/theme/studio/stackScreenShell';

// ——— layout tokens (v3 only) ———
const HUB_RADIUS = 22;
const HUB_TILE_ICON = 42;
const HUB_TILE_ICON_GLYPH = 24;
/** Scroll padding from the physical screen edge. */
const GUTTER = appTheme.spacing.lg;
/** Inset of content inside list cards, hero, and nav rows. */
const LIST_INNER = appTheme.spacing.md;
/** v3 account hub: compact social pills. */
const GRID_GAP = appTheme.spacing.md;
const TILE_TITLE_LINE_HEIGHT = androidScaleFont(22);
const TILE_TITLE_MAX_LINES = 2;
const TILE_SUBTITLE_LINE_HEIGHT = androidScaleFont(18);
const TILE_ICON_TITLE_GAP = appTheme.spacing.sm;
const TILE_TITLE_SUBTITLE_GAP = appTheme.spacing.xxs;
const TILE_MIN_HEIGHT =
  appTheme.spacing.sm * 2 +
  HUB_TILE_ICON +
  TILE_ICON_TITLE_GAP +
  TILE_TITLE_LINE_HEIGHT +
  TILE_TITLE_SUBTITLE_GAP +
  TILE_SUBTITLE_LINE_HEIGHT;

const PLAN_THUMB = 92;

const styles = StyleSheet.create({
  sectionLabel: {
    ...appTheme.typography.captionSmall,
    fontSize: androidScaleFont(11),
    fontWeight: '700',
    letterSpacing: 0.6,
    marginBottom: appTheme.spacing.sm,
    paddingHorizontal: appTheme.spacing.sm,
    textTransform: 'uppercase',
  },
  irregularGridWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: GRID_GAP,
  },
  irregularGridWrapRtl: {
    /** Pack cells from the trailing edge so the first tile sits top-start in RTL. */
    ...layoutRtlStyle,
  },
  irregularGridCell: {
    flexBasis: '47%',
    flexGrow: 1,
    minWidth: 0,
    alignSelf: 'stretch',
    /** Keep each tile’s internal layout independent of the grid’s RTL direction. */
    ...layoutLtrStyle,
  },
  hubTile: {
    flex: 1,
    width: '100%',
    borderRadius: HUB_RADIUS,
    padding: appTheme.spacing.md,
    minHeight: TILE_MIN_HEIGHT,
    overflow: 'visible',
  },
  hubTileTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: TILE_ICON_TITLE_GAP,
  },
  hubTileTopRtl: {
    flexDirection: 'row-reverse',
  },
  hubTileContent: {
    flex: 1,
    minWidth: 0,
  },
  hubTileIcon: {
    width: HUB_TILE_ICON,
    height: HUB_TILE_ICON,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hubTileBadge: {
    borderRadius: 999,
    paddingHorizontal: appTheme.spacing.sm,
    paddingVertical: 2,
    minWidth: 18,
    alignItems: 'center',
  },
  hubTileBadgeSpacer: {
    width: 18,
    height: 18,
  },
  hubTileBadgeText: {
    ...appTheme.typography.captionSmall,
    fontWeight: '800',
  },
  hubTileTitle: {
    ...appTheme.typography.bodyMedium,
    fontSize: androidScaleFont(15),
    fontWeight: '800',
    letterSpacing: 0.2,
    lineHeight: TILE_TITLE_LINE_HEIGHT,
  },
  hubTileTitleBlock: {
    justifyContent: 'flex-start',
  },
  hubTileSubtitle: {
    ...appTheme.typography.bodySmall,
    fontSize: androidScaleFont(13),
    lineHeight: TILE_SUBTITLE_LINE_HEIGHT,
  },
  hubTileSubtitleBlock: {
    marginTop: TILE_TITLE_SUBTITLE_GAP,
    justifyContent: 'flex-start',
  },
  planHeroCta: {
    marginTop: appTheme.spacing.xs,
  },
  planHeroLoading: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: androidScaleSize(112),
    padding: LIST_INNER,
  },
  /** Shadow/elevation live here — never set overflow:hidden on this shell. */
  planHeroCard: {
    borderRadius: HUB_RADIUS,
  },
  /** Clips the flush edge image to the card radius without affecting the outer shadow. */
  planHeroClip: {
    borderRadius: HUB_RADIUS,
    overflow: 'hidden',
  },
  planHeroInnerPadded: {
    padding: LIST_INNER,
    gap: appTheme.spacing.md,
  },
  planHeroShell: {
    flexDirection: 'row',
    alignItems: 'stretch',
    minHeight: androidScaleSize(112),
  },
  planHeroShellRtl: {
    flexDirection: 'row-reverse',
  },
  planThumbEdge: {
    width: androidScaleSize(100),
    alignSelf: 'stretch',
    overflow: 'hidden',
    flexShrink: 0,
    position: 'relative',
    borderTopLeftRadius: HUB_RADIUS,
    borderBottomLeftRadius: HUB_RADIUS,
  },
  planThumbEdgeRtl: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: HUB_RADIUS,
    borderBottomRightRadius: HUB_RADIUS,
  },
  planThumbInset: {
    width: androidScaleSize(PLAN_THUMB),
    height: androidScaleSize(PLAN_THUMB),
    borderRadius: androidScaleSize(16),
    overflow: 'hidden',
    flexShrink: 0,
    position: 'relative',
  },
  planThumbImage: {
    ...StyleSheet.absoluteFillObject,
  },
  planThumbFallback: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  planHeroBody: {
    flex: 1,
    minWidth: 0,
    paddingVertical: LIST_INNER,
    paddingStart: appTheme.spacing.md,
    paddingEnd: LIST_INNER,
    gap: appTheme.spacing.sm,
    justifyContent: 'center',
  },
  planHeroEmptyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: appTheme.spacing.md,
  },
  planHeroEmptyRowRtl: {
    flexDirection: 'row-reverse',
  },
  planHeroTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: appTheme.spacing.sm,
  },
  planHeroCopy: {
    flex: 1,
    minWidth: 0,
    gap: 3,
    justifyContent: 'center',
  },
  planEyebrow: {
    fontSize: androidScaleFont(11),
    fontWeight: '800',
    letterSpacing: 0.7,
    textTransform: 'uppercase',
  },
  planTitle: {
    fontSize: androidScaleFont(17),
    fontWeight: '800',
    letterSpacing: -0.3,
    lineHeight: androidScaleFont(22),
  },
  planStatRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 1,
  },
  planStatRowRtl: {
    flexDirection: 'row-reverse',
  },
  planStatValue: {
    fontSize: androidScaleFont(20),
    fontWeight: '800',
    letterSpacing: -0.4,
  },
  planStatLabel: {
    fontSize: androidScaleFont(13),
    fontWeight: '600',
  },
  planDateCaption: {
    ...appTheme.typography.caption,
    fontSize: androidScaleFont(12),
    lineHeight: androidScaleFont(16),
  },
  planHeroFooter: {
    gap: 6,
  },
  progressTrack: {
    ...layoutLtrStyle,
    height: 6,
    borderRadius: 100,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 100,
  },
  planHeroChevron: {
    width: androidScaleSize(30),
    height: androidScaleSize(30),
    borderRadius: androidScaleSize(15),
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 2,
  },
  socialSection: {
    marginTop: appTheme.spacing.sm,
  },
  socialCard: {
    borderRadius: HUB_RADIUS,
    paddingVertical: LIST_INNER,
    paddingHorizontal: LIST_INNER,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch',
    width: '100%',
  },
  socialRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: appTheme.spacing.md,
    width: '100%',
  },
  /** Equal columns — matches set1 so pills fill the card and stay centered. */
  socialPillCell: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    justifyContent: 'center',
  },
  socialPillHit: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'stretch',
  },
  socialPill: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: appTheme.spacing.xs,
    borderRadius: 999,
    paddingHorizontal: appTheme.spacing.sm,
    paddingVertical: appTheme.spacing.sm,
    width: '100%',
  },
  socialPillText: {
    ...appTheme.typography.captionSmall,
    fontSize: androidScaleFont(11),
    lineHeight: androidScaleFont(16),
    fontWeight: '700',
    flexShrink: 1,
    minWidth: 0,
    textAlign: 'center',
    includeFontPadding: false,
    textAlignVertical: 'center',
  },
  footer: {
    alignItems: 'center',
    marginTop: appTheme.spacing.lg,
    marginBottom: appTheme.spacing.xs,
  },
  logoutIconOptical: {
    transform: [{ translateX: 1.5 }],
  },
});

function PlanThumb({
  uri,
  colors,
  edge = false,
  rtl = false,
}: {
  uri: string | null;
  colors: ThemeColors;
  /** Flush leading-edge photo for the active-plan shell. */
  edge?: boolean;
  rtl?: boolean;
}) {
  const [image_error, set_image_error] = useState(false);
  const show_image = Boolean(uri) && !image_error;

  return (
    <View style={[edge ? styles.planThumbEdge : styles.planThumbInset, edge && rtl ? styles.planThumbEdgeRtl : null]}>
      {show_image ? (
        <Image source={{ uri: uri! }} style={styles.planThumbImage} resizeMode="cover" onError={() => set_image_error(true)} />
      ) : (
        <View style={[styles.planThumbFallback, { backgroundColor: colors.surfaceElevated }]}>
          <SvgIcon name="meal" size={28} color={colors.primary} />
        </View>
      )}
    </View>
  );
}

function AccountsPlanHeroCard({
  activeSub,
  plan_image_uri,
  subscription_title,
  days_left,
  progress_pct,
  colors,
  text_align,
  writing_direction,
  disclosure,
  on_press,
  is_loading,
  active_plan_label,
  browse_plans_label,
  days_left_label,
  explore_label,
  no_active_plans_label,
  purchase_plan_label,
}: {
  activeSub?: SubscriptionData;
  plan_image_uri: string | null;
  subscription_title: string | null;
  days_left: number;
  progress_pct: number;
  colors: ThemeColors;
  text_align: 'left' | 'right';
  writing_direction: 'ltr' | 'rtl';
  disclosure: 'arrow-left' | 'arrow-right';
  on_press: () => void;
  is_loading: boolean;
  active_plan_label: string;
  browse_plans_label: string;
  days_left_label: string;
  explore_label: string;
  no_active_plans_label: string;
  purchase_plan_label: string;
}) {
  const has_plan = Boolean(activeSub);
  const card_shell = [
    styles.planHeroCard,
    studioCardShell(colors),
    { backgroundColor: colors.surface, shadowColor: colors.shadowColor },
  ];

  if (is_loading) {
    return (
      <View style={card_shell}>
        <View style={styles.planHeroClip}>
          <View style={styles.planHeroLoading}>
            <ButtonLoader size={28} color={colors.primary} />
          </View>
        </View>
      </View>
    );
  }

  if (!has_plan) {
    return (
      <View style={card_shell}>
        <View style={styles.planHeroClip}>
          <View style={styles.planHeroInnerPadded}>
            <View style={[styles.planHeroEmptyRow, writing_direction === 'rtl' ? styles.planHeroEmptyRowRtl : null]}>
              <PlanThumb uri={null} colors={colors} rtl={writing_direction === 'rtl'} />
              <View style={styles.planHeroCopy}>
                <Text
                  style={[
                    styles.planEyebrow,
                    { color: colors.textSecondary, textAlign: text_align, writingDirection: writing_direction },
                  ]}
                  numberOfLines={1}>
                  {browse_plans_label}
                </Text>
                <Text
                  style={[styles.planTitle, { color: colors.text, textAlign: text_align, writingDirection: writing_direction }]}
                  numberOfLines={2}>
                  {no_active_plans_label}
                </Text>
                <Text
                  style={[
                    styles.planDateCaption,
                    { color: colors.textSecondary, textAlign: text_align, writingDirection: writing_direction },
                  ]}
                  numberOfLines={2}>
                  {explore_label}
                </Text>
              </View>
            </View>
            <SolidCtaClickable
              label={purchase_plan_label}
              onPress={on_press}
              size="compact"
              haptics
              style={styles.planHeroCta}
            />
          </View>
        </View>
      </View>
    );
  }

  const plan_title = subscription_title ?? active_plan_label;

  return (
    <Clickable2
      onPress={on_press}
      accessibilityLabel={plan_title}
      accessibilityRole="button"
      haptics
      style={card_shell}>
      <View style={styles.planHeroClip}>
        <View style={[styles.planHeroShell, writing_direction === 'rtl' ? styles.planHeroShellRtl : null]}>
          <PlanThumb uri={plan_image_uri} colors={colors} edge rtl={writing_direction === 'rtl'} />
          <View style={styles.planHeroBody}>
            <View style={[styles.planHeroTopRow, writing_direction === 'rtl' ? { flexDirection: 'row-reverse' } : null]}>
              <View style={styles.planHeroCopy}>
                <Text
                  style={[
                    styles.planEyebrow,
                    { color: colors.textSecondary, textAlign: text_align, writingDirection: writing_direction },
                  ]}
                  numberOfLines={1}>
                  {active_plan_label}
                </Text>
                <Text
                  style={[styles.planTitle, { color: colors.text, textAlign: text_align, writingDirection: writing_direction }]}
                  numberOfLines={2}>
                  {plan_title}
                </Text>
                <View
                  style={[
                    styles.planStatRow,
                    writing_direction === 'rtl' ? styles.planStatRowRtl : null,
                    { justifyContent: text_align === 'right' ? 'flex-end' : 'flex-start' },
                  ]}>
                  <Text
                    style={[styles.planStatValue, { color: colors.primary, writingDirection: writing_direction }]}>
                    {formatNumber(days_left)}
                  </Text>
                  <Text
                    style={[styles.planStatLabel, { color: colors.textSecondary, writingDirection: writing_direction }]}
                    numberOfLines={1}>
                    {days_left_label}
                  </Text>
                </View>
              </View>
              <View style={[styles.planHeroChevron, { backgroundColor: colors.surfaceElevated }]}>
                <SvgIcon name={disclosure} size={16} color={colors.textLight} />
              </View>
            </View>
            <View style={styles.planHeroFooter}>
              <View style={[styles.progressTrack, { backgroundColor: colors.surfaceElevated }]}>
                <View
                  style={[
                    styles.progressFill,
                    {
                      width: `${progress_pct}%`,
                      backgroundColor: colors.primary,
                      alignSelf: writing_direction === 'rtl' ? 'flex-end' : 'flex-start',
                    },
                  ]}
                />
              </View>
              {activeSub?.start_date && activeSub?.end_date ? (
                <Text
                  style={[
                    styles.planDateCaption,
                    { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction },
                  ]}
                  numberOfLines={1}>
                  {activeSub.start_date} – {activeSub.end_date}
                </Text>
              ) : null}
            </View>
          </View>
        </View>
      </View>
    </Clickable2>
  );
}

type NavRow = {
  icon: string;
  title: string;
  subtitle: string;
  onPress: () => void;
  isDisabled?: boolean;
  badge?: number;
};

function AccountHubGrid({
  rows,
  colors,
  text_align,
  writing_direction,
  rtl_wrap,
}: {
  rows: NavRow[];
  colors: ThemeColors;
  text_align: 'left' | 'right';
  writing_direction: 'ltr' | 'rtl';
  rtl_wrap: boolean;
}) {
  if (rows.length === 0) {
    return null;
  }
  return (
    <View style={[styles.irregularGridWrap, rtl_wrap ? styles.irregularGridWrapRtl : null]}>
      {rows.map(row => (
        <View key={row.title} style={styles.irregularGridCell}>
          <AccountActionTile row={row} colors={colors} text_align={text_align} writing_direction={writing_direction} />
        </View>
      ))}
    </View>
  );
}

function AccountActionTile({
  row,
  colors,
  text_align,
  writing_direction,
}: {
  row: NavRow;
  colors: ThemeColors;
  text_align: 'left' | 'right';
  writing_direction: 'ltr' | 'rtl';
}) {
  return (
    <Clickable2
      onPress={row.isDisabled ? undefined : row.onPress}
      disabled={row.isDisabled}
      accessibilityLabel={row.title}
      accessibilityRole="button"
      haptics
      style={[
        styles.hubTile,
        studioCardShell(colors),
        {
          backgroundColor: colors.surface,
          shadowColor: colors.shadowColor,
          minHeight: TILE_MIN_HEIGHT,
        },
        row.isDisabled ? { opacity: 0.55 } : null,
      ]}>
      <View style={[styles.hubTileTop, writing_direction === 'rtl' ? styles.hubTileTopRtl : null]}>
        <View style={[styles.hubTileIcon, { backgroundColor: colors.surfaceElevated }]}>
          {row.isDisabled ? <ButtonLoader size={18} color={colors.primary} /> : <SvgIcon name={row.icon} size={HUB_TILE_ICON_GLYPH} color={colors.primary} />}
        </View>
        {row.badge != null && row.badge > 0 ? (
          <View style={[styles.hubTileBadge, { backgroundColor: colors.error }]}>
            <Text style={[styles.hubTileBadgeText, { color: colors.onError }]}>{row.badge}</Text>
          </View>
        ) : (
          <View style={styles.hubTileBadgeSpacer} />
        )}
      </View>
      <View style={styles.hubTileContent}>
      <View style={styles.hubTileTitleBlock}>
        <Text
          style={[styles.hubTileTitle, { color: colors.text, textAlign: text_align, writingDirection: writing_direction }]}
          numberOfLines={TILE_TITLE_MAX_LINES}>
          {row.title}
        </Text>
      </View>
      <View style={styles.hubTileSubtitleBlock}>
        <Text
          style={[styles.hubTileSubtitle, { color: colors.textSecondary, textAlign: text_align, writingDirection: writing_direction }]}
          numberOfLines={1}>
          {row.subtitle ?? ' '}
        </Text>
      </View>
      </View>
    </Clickable2>
  );
}

function SectionLabel({
  label,
  color,
  text_align,
  writing_direction,
}: {
  label: string;
  color: string;
  text_align: 'left' | 'right' | 'center';
  writing_direction: 'ltr' | 'rtl';
}) {
  return (
    <Text
      style={[
        styles.sectionLabel,
        { color, textAlign: text_align, writingDirection: writing_direction },
      ]}>
      {label}
    </Text>
  );
}

export const AccountsScreenSet3: React.FC = () => {
  const navigation = useAuthNavigation();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const colors = useThemeColors() ?? appTheme.colors;
  const rtl = useRtl();

  const user = useAuthStore(s => s.user);
  const { activeSubscriptions, availableDates, subscriptions, dayConfigMap, config, isLoading: is_loading_subscriptions } = useSubscriptionContext();
  const { data: catalog_plans } = useSubscriptionPlans();
  const activeSub = activeSubscriptions[0];
  const meal_progress = useMemo(
    () =>
      activeSub
        ? getActiveMealDayProgressFromSubscription(activeSub, availableDates, subscriptions, dayConfigMap, config)
        : null,
    [activeSub, availableDates, subscriptions, dayConfigMap, config],
  );
  const daysLeft = meal_progress?.daysLeft ?? 0;
  const pausedDaysLeft = meal_progress?.pausedDaysLeft ?? 0;
  const progress_pct = Math.max(0, Math.min(100, meal_progress?.progressPercentage ?? 0));
  const days_left_label =
    pausedDaysLeft > 0
      ? t('calendar.deliveryDaysHeroCaptionPaused', { paused: pausedDaysLeft })
      : t('account.daysLeft');

  const plan_image_uri = useMemo(() => {
    if (!activeSub) {
      return null;
    }
    return resolveMergedSubscriptionImageUri(activeSub, catalog_plans);
  }, [activeSub, catalog_plans]);

  const show_community = useCommunityFeatureEnabled();
  const show_consultations = useConsultationsFeatureEnabled();
  const show_online_orders = useOnlineOrdersFeatureEnabled();
  const show_loyalty = useLoyaltyFeatureEnabled();
  const { organization } = useApiConfig();
  const social_links = useMemo(() => resolveOrganizationSocialLinks(), [organization]);
  const text_align: 'left' | 'right' = rtl.isRTL ? 'right' : 'left';
  const handleLogout = useCallback(async () => {
    await logout();
  }, []);

  const openSocialLink = (url: string) => {
    Linking.openURL(url).catch(() => {});
  };

  const nav = useCallback(
    (route: string, params?: object) => (navigation as { navigate: (r: string, p?: object) => void }).navigate(route, params),
    [navigation],
  );

  const subscription_title = activeSub ? getSubscriptionPlanTitleFromMerged(activeSub, t('account.activePlan')) : null;
  const profile_name = user?.name || t('account.title');

  const handle_plan_hero_press = useCallback(() => {
    nav(activeSub ? 'MySubscriptions' : 'SubscriptionList');
  }, [activeSub, nav]);

  const main_pad = { paddingHorizontal: GUTTER };

  const acc_account_rows: NavRow[] = useMemo(() => {
    const rows: NavRow[] = [
      { icon: 'menu', title: t('account.mySubscriptions'), subtitle: t('account.manageYourPlans'), onPress: () => nav('MySubscriptions') },
    ];
    if (show_online_orders) {
      rows.push({
        icon: 'time',
        title: t('account.orderHistory'),
        subtitle: t('account.viewPastOrders'),
        onPress: () => nav('OrderHistory'),
      });
    }
    rows.push(
      { icon: 'map', title: t('account.addresses'), subtitle: t('account.manageDeliveryAddresses'), onPress: () => nav('AddressLists') },
      { icon: 'settings', title: t('account.settings'), subtitle: t('account.languageAndPreferences'), onPress: () => nav('Settings') },
    );
    if (isDevHubClient()) {
      rows.push({
        icon: 'settings',
        title: 'Dev Tools',
        subtitle: 'API target, perf toggles',
        onPress: () => nav('DevTools'),
      });
    }
    return rows;
  }, [t, nav, show_online_orders]);

  const support_rows: NavRow[] = useMemo(() => {
    const base: NavRow[] = [];
    if (show_consultations) {
      base.push({
        icon: 'chat',
        title: t('account.consultation'),
        subtitle: t('account.bookASession'),
        onPress: () => nav('Consultation'),
      });
    }
    base.push(
      { icon: 'bell', title: t('account.notifications'), subtitle: t('account.manageNotifications'), onPress: () => nav('Notifications') },
      { icon: 'list', title: t('account.allergiesAndDislikes'), subtitle: t('account.manageAllergiesDislikes'), onPress: () => nav('AllergiesDislikes') },
    );
    if (show_community) {
      base.push({
        icon: 'chat',
        title: t('account.community'),
        subtitle: t('account.communitySubtitle'),
        onPress: () => nav('Community'),
      });
    }
    return base;
  }, [t, nav, show_community, show_consultations]);

  const plan_rows: NavRow[] = useMemo(
    () => {
      const rows: NavRow[] = [
        { icon: 'user', title: t('account.profile'), subtitle: t('account.editYourInformation'), onPress: () => nav('Profile') },
      ];
      if (show_loyalty) {
        rows.push({ icon: 'gift', title: t('account.loyaltyRewards'), subtitle: t('account.viewYourPoints'), onPress: () => nav('Loyalty') });
      }
      rows.push({ icon: 'list', title: t('account.browsePlans'), subtitle: t('account.exploreSubscriptionOptions'), onPress: () => nav('SubscriptionList') });
      return rows;
    },
    [t, nav, show_loyalty],
  );

  const content_top = useHubHeaderTopPadding(TAB_HEADER_EXPANDED_HEIGHT, appTheme.spacing.sm);
  const scroll_y = useSharedValue(0);
  const on_account_scroll = useAnimatedScrollHandler({
    onScroll: (e) => {
      scroll_y.value = e.contentOffset.y;
    },
  });
  const header_logout = (
    <HeaderIconButton onPress={handleLogout} accessibilityLabel={t('account.logOut')} appearance="elevated" haptics>
      {/**
       * Door+arrow logout art is optically left-heavy in the 24×24 viewBox —
       * nudge so it reads centered inside the circular shell.
       */}
      <SvgIcon name="logout" size={22} color={colors.text} style={styles.logoutIconOptical} />
    </HeaderIconButton>
  );

  return (
    <ScreenShell colors={colors}>
    <View style={{ flex: 1, backgroundColor: 'transparent' }}>
      <ScreenHeader
        title={t('account.title')}
        subtitle={profile_name}
        collapseOnScroll
        scrollY={scroll_y}
        expandedHeight={TAB_HEADER_EXPANDED_HEIGHT}
        textAlign={text_align}
        rightAction={header_logout}
      />
      <Animated.ScrollView
        style={{ flex: 1, zIndex: 1 }}
        onScroll={on_account_scroll}
        scrollEventThrottle={16}
        contentInsetAdjustmentBehavior="never"
        automaticallyAdjustContentInsets={false}
        contentContainerStyle={[
          main_pad,
          {
            gap: DASHBOARD_SECTION_GAP,
            paddingTop: content_top,
            paddingBottom: insets.bottom + appTheme.spacing.xxl,
          },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        bounces
        overScrollMode="auto">
        <AnimatedSection {...HUB_SECTION_PROPS} index={0}>
          <AccountsPlanHeroCard
            activeSub={activeSub}
            plan_image_uri={plan_image_uri}
            subscription_title={subscription_title}
            days_left={daysLeft}
            progress_pct={progress_pct}
            colors={colors}
            text_align={text_align}
            writing_direction={rtl.isRTL ? 'rtl' : 'ltr'}
            disclosure={rtl.trailingRowDisclosureChevron}
            on_press={handle_plan_hero_press}
            is_loading={is_loading_subscriptions}
            active_plan_label={t('account.activePlan')}
            browse_plans_label={t('account.browsePlans')}
            days_left_label={days_left_label}
            explore_label={t('account.exploreSubscriptionOptions')}
            no_active_plans_label={t('subscriptions.noActivePlans')}
            purchase_plan_label={t('subscriptions.explorePlans')}
          />
        </AnimatedSection>

        <AnimatedSection {...HUB_SECTION_PROPS} index={1}>
          <SectionLabel
            label={t('account.accountSection')}
            color={colors.textLight}
            text_align={text_align}
            writing_direction={rtl.isRTL ? 'rtl' : 'ltr'}
          />
          <AccountHubGrid
            rows={acc_account_rows}
            colors={colors}
            text_align={text_align}
            writing_direction={rtl.isRTL ? 'rtl' : 'ltr'}
            rtl_wrap={rtl.isRTL}
          />
        </AnimatedSection>

        <AnimatedSection {...HUB_SECTION_PROPS} index={2}>
          <SectionLabel
            label={t('account.supportSection')}
            color={colors.textLight}
            text_align={text_align}
            writing_direction={rtl.isRTL ? 'rtl' : 'ltr'}
          />
          <AccountHubGrid
            rows={support_rows}
            colors={colors}
            text_align={text_align}
            writing_direction={rtl.isRTL ? 'rtl' : 'ltr'}
            rtl_wrap={rtl.isRTL}
          />
        </AnimatedSection>

        <AnimatedSection {...HUB_SECTION_PROPS} index={3}>
          <SectionLabel
            label={t('account.subscriptionSection')}
            color={colors.textLight}
            text_align={text_align}
            writing_direction={rtl.isRTL ? 'rtl' : 'ltr'}
          />
          <AccountHubGrid
            rows={plan_rows}
            colors={colors}
            text_align={text_align}
            writing_direction={rtl.isRTL ? 'rtl' : 'ltr'}
            rtl_wrap={rtl.isRTL}
          />
        </AnimatedSection>

        {social_links.length > 0 ? (
        <AnimatedSection {...HUB_SECTION_PROPS} index={4}>
          <View style={[styles.socialSection, styles.socialCard, studioCardShell(colors), { backgroundColor: colors.surface }]}>
            <View style={[styles.socialRow, rtl.layoutLtrStyle]}>
              {social_links.map(link => (
                <View key={link.key} style={styles.socialPillCell}>
                  <Clickable2
                    style={styles.socialPillHit}
                    onPress={() => openSocialLink(link.url)}
                    accessibilityLabel={link.label}
                    accessibilityRole="link"
                    haptics>
                    <View style={[styles.socialPill, { backgroundColor: colors.surfaceElevated }]}>
                      <SvgIcon
                        name={link.icon}
                        size={16}
                        color={link.key === 'whatsapp' ? '#25D366' : link.key === 'instagram' ? '#E4405F' : '#FF0000'}
                      />
                      <Text style={[styles.socialPillText, { color: colors.text }]} numberOfLines={1} ellipsizeMode="tail">
                        {link.label}
                      </Text>
                    </View>
                  </Clickable2>
                </View>
              ))}
            </View>
          </View>
        </AnimatedSection>
        ) : null}

        <View style={styles.footer}>
          <Text style={[appTheme.typography.captionSmall, { color: colors.textLight, textAlign: 'center' }]}>
            {t('account.version')} 1.0.0
          </Text>
        </View>
      </Animated.ScrollView>
    </View>
    </ScreenShell>
  );
};

export default AccountsScreenSet3;
